/**
 * Provides basic show/hide functionality to elements based on an attribute value.
 *
 * Usage:
 *
 * <select class="select">
 *     <option value="">All categories</option>
 *     <option value="category-1">Category 1</option>
 *     <option value="category-2">Category 2</option>
 * </select>
 *
 * <div class="element" data-category="category-1"></div>
 * <div class="element" data-category="category-2"></div>
 * <div class="element" data-category="category-1 category-2"></div>
 *
 * const select = document.querySelector('.select');
 * const elements = document.querySelectorAll('.element');
 * new CategoryFilter(select, elements);
 */
export default class CategoryFilter {
	/** The select element which controls the category. */
	private select: HTMLSelectElement;

	/** The elements which will have their visibility toggled. */
	private elements: NodeListOf<HTMLElement>;

	constructor(select: HTMLSelectElement, elements: NodeListOf<HTMLElement>) {
		this.select = select;
		this.elements = elements;

		this.disableUselessOptions();

		// Update the visibility of the elements whenever the value in the select element changes
		this.select.addEventListener('change', (event) => {
			const category = (event.target as HTMLSelectElement).value;
			this.updateElements(category);
		});
	}

	/** Whether the provided element has the provided category. */
	static hasCategory(element: HTMLElement, category: string): boolean {
		return element.matches(`[data-category~="${category}"]`);
	}

	/** Makes the provided element visible. */
	static showElement(element: HTMLElement): void {
		element.style.display = 'block';
	}

	/** Makes the provided element hidden. */
	static hideElement(element: HTMLElement): void {
		element.style.display = 'none';
	}

	/** Shows/hides elements based on whether they match the selected category. */
	updateElements(category: string): void {
		this.elements.forEach((element) => {
			// The 'All Categories' option, if present, should have a value of ''
			if (category === '') {
				CategoryFilter.showElement(element);
				return;
			}

			const hasCategory = CategoryFilter.hasCategory(element, category);
			hasCategory ? CategoryFilter.showElement(element) : CategoryFilter.hideElement(element);
		});
	}

	/** Disables the options which do not yield any results. */
	disableUselessOptions(): void {
		/** The array of categories that are found on elements. */
		const foundCategories = [];

		// Iterate through the elements and record the found categories
		Array.from(this.elements).forEach((event) => {
			// Get all the categories from the data attribute
			const categories = event.dataset.category.split(' ');

			categories.forEach((category) => {
				// Ignore this item if it's an empty string
				if (category === '') return;

				foundCategories.push(category);
			});
		});

		/** The node list of options within the select element. */
		const options = this.select.querySelectorAll('option');

		// Iterate through the options and disable the useless ones
		Array.from(options).forEach((option) => {
			// Ignore the option if its value is '' (this should be the 'All Categories' option)
			if (option.value === '') return;

			// Ignore the option if it was found on at least one element
			if (foundCategories.includes(option.value)) return;

			option.disabled = true;
		});
	}
}
